import {Component, Prop, Watch} from 'vue-property-decorator';
import {CLEAR_BUYER_DATA, CLEAR_IS_GUEST_ORDER, DELETE_CART, UPDATE_CART} from '../store/commits';
import {VuexObjectMixin} from './VuexObjectMixin';
import {OrderDTO, TicketConfigurationDTO} from '../DTOs';
import {Client, Event, OrderItem, Product, Slot, TicketConfiguration} from '../Models';
import {CartTimeUtility, UUID4Utility} from '../Utilities';
import {ApiErrorHandler} from '../Errors/ApiErrorHandler';
import {mapGetters} from 'vuex';

@Component({
  computed: {
    ...mapGetters({
      isGuestOrder: 'getIsGuestOrder',
    }),
  },
})
export class CartMixin extends VuexObjectMixin {
  @Prop({type: String, required: true}) inventoryUrl: string | undefined;
  @Prop({required: false, type: Boolean, default: false}) checkoutView: Boolean | undefined;

  isGuestOrder: boolean | undefined;

  protected initializing: boolean = false;
  protected loading: boolean = false;

  protected ticketConfigurations: Array<TicketConfiguration> = [];
  protected advertisedOrderItems: Array<OrderItem> = [];
  protected editableTicketConfigurations: Array<TicketConfiguration> = [];

  // These are products from e.g. codes, which can only contain a quantity of one and are not editable
  protected fixedTicketConfigurations: Array<TicketConfiguration> = [];

  protected orderDTO: OrderDTO = new OrderDTO();

  protected orderChangedDate: Date | null = null;
  protected cartTimeInterval: number | null = null;

  protected productPromotionMapping: Array<any> = [];

  // this is only used to trigger a rerender of the computed properties to refresh the remaining time
  protected triggerRefresh: boolean = false;
  protected intervalSet: boolean = false;

  created() {
    this.orderDTO = new OrderDTO();
    this.productPromotionMapping = [];
  }

  async mounted() {
    if (this.currentOrder) {
      this.initializing = true;
      await this.updateTicketConfigurations();
      this.initializing = false;
    }
  }

  beforeDestroy() {
    if (this.cartTimeInterval) {
      clearInterval(this.cartTimeInterval);
    }
  }

  setCartTimeInterval() {
    if (!this.cartTimeInterval) {
      // @ts-ignore
      this.cartTimeInterval = setInterval(
        () => {
          this.triggerRefresh = !this.triggerRefresh;
          if (this.currentOrder && (!this.orderChangedDate || CartTimeUtility.getInstance().getRemainingTime(this.orderChangedDate) <= 0)) {
            this.deleteOrder();
            if (this.cartTimeInterval) {
              clearInterval(this.cartTimeInterval);
            }
          }
        },
        1000,
      );
    }
  }

  fillEditableConfigurations() {
    this.editableTicketConfigurations = [];
    this.ticketConfigurations.forEach((ticketConfiguration) => {
      const clonedConfiguration = new TicketConfiguration(
        ticketConfiguration.id,
        ticketConfiguration.product,
        ticketConfiguration.event,
        ticketConfiguration.slot,
        ticketConfiguration.specialProduct,
        ticketConfiguration.personalUse,
        null,
        ticketConfiguration.subTicketConfigurations,
        ticketConfiguration.promotion,
        ticketConfiguration.registrationRequired,
      );
      clonedConfiguration.quantity = ticketConfiguration.quantity;
      clonedConfiguration.initialQuantity = ticketConfiguration.initialQuantity;
      clonedConfiguration.isPromoted = ticketConfiguration.isPromoted;
      clonedConfiguration.createdFrom = ticketConfiguration.createdFrom;
      this.editableTicketConfigurations.push(clonedConfiguration);
    });
  }

  addSpecialTicketConfigurations() {
    const advertisedProductIds = this.cart!.advertised.map(a => a.productId);
    const advertisedProducts = this.products.filter(p => advertisedProductIds.includes(<number>p.id));

    for (let orderItem of this.advertisedOrderItems) {
      if (this.ticketConfigurations.find((ticketConfig: TicketConfiguration) => ticketConfig.product.id === orderItem.productId)) {
        continue;
      }
      const product = advertisedProducts.find(p => p.id === orderItem.productId);
      if (!product) {
        throw new Error('Unknown advertised product ' + orderItem.productId);
      }

      let relatedEvent: Event | undefined = this.events.find((event) => event.id === product.eventId);
      if (!relatedEvent) {
        throw new Error('Unknown event for advertised product ' + product.descriptionWebShop);
      }

      const ticketConfiguration = new TicketConfiguration(
        orderItem.id,
        product,
        relatedEvent,
        null,
        true,
        orderItem.personalUse,
        null,
        [],
        null,
        product.registrationRequired);
      ticketConfiguration.quantity = orderItem.quantity;
      this.ticketConfigurations.push(ticketConfiguration);
    }
  }

  async updateTicketConfigurations(): Promise<void> {
    this.loading = true;
    if (!this.cart || !this.currentOrder) {
      return;
    }

    this.ticketConfigurations = [];
    this.advertisedOrderItems = [];
    this.productPromotionMapping = [];
    if (this.cart.promotedProducts.length > 0) {
      this.cart.promotedProducts.forEach((promotedProduct: any) => {
        // Clone this, because otherwise the objects inside the cart overwrite each other
        // Always keep in mind we have the mini-cart and we have the big cart sharing the same objects from vuex
        const clonedPromotion = JSON.parse(JSON.stringify(promotedProduct));
        clonedPromotion.used = false;
        this.productPromotionMapping.push(clonedPromotion);
      });
    }
    this.orderChangedDate = new Date(this.currentOrder.changedDate);
    this.setCartTimeInterval();
    this.currentOrder.orderItems.forEach((orderItem: OrderItem) => {
      const matching11ProductPromotions = this.productPromotionMapping
        .filter(p => p.productId === orderItem.productId && p.used === false && p.promotionCodeType === 2);
      const matching1NProductPromotions = this.productPromotionMapping
        .filter(p => p.productId === orderItem.productId && p.used === false && p.promotionCodeType === 1);
      if (matching1NProductPromotions.length > 0 || matching11ProductPromotions.length > 0) {
        matching11ProductPromotions.forEach(p => {
          p.used = true;
        });
        matching1NProductPromotions.forEach(p => {
          p.used = true;
        });
        const promoted11Quantity = matching11ProductPromotions.length;
        const promoted1NQuantity = matching1NProductPromotions.length;
        matching11ProductPromotions.forEach(p => {
          const promotedTicketConfiguration = this.createPromotionTicketConfigurationFromOrderItem(orderItem, p, 1);
          if (promotedTicketConfiguration) {
            this.ticketConfigurations.push(promotedTicketConfiguration);
          }
        });
        if (promoted1NQuantity > 0) {
          const promotedTicketConfiguration = this.createPromotionTicketConfigurationFromOrderItem(
            orderItem,
            matching1NProductPromotions[0],
            promoted1NQuantity,
          );
          if (promotedTicketConfiguration) {
            this.ticketConfigurations.push(promotedTicketConfiguration);
          }
        }

        // We need to split the promoted products from the normal products for display and usability reasons
        const remainingQuantity = orderItem.quantity - (promoted11Quantity + promoted1NQuantity);
        if (remainingQuantity > 0) {
          const regularTicketConfiguration = this.createTicketConfigurationFromOrderItem(orderItem, null, remainingQuantity);
          if (regularTicketConfiguration) {
            this.ticketConfigurations.push(regularTicketConfiguration);
          }
        }
      } else {
        const ticketConfiguration = this.createTicketConfigurationFromOrderItem(orderItem);
        if (ticketConfiguration === null) {
          this.advertisedOrderItems.push(orderItem);
        } else {
          this.ticketConfigurations.push(ticketConfiguration);
        }
      }
    });

    this.orderDTO = this.orderDTO.fromModel(this.currentOrder);
    this.addSpecialTicketConfigurations();
    this.fillEditableConfigurations();
    this.loading = false;
  }

  createPromotionTicketConfigurationFromOrderItem(orderItem: OrderItem, promotion, quantity: number): TicketConfiguration | null {
    const matchingProduct = this.products.find(prod => prod.id === orderItem.productId);
    let matchingEvent: Event | undefined;
    if (matchingProduct) {
      matchingEvent = this.events.find(evt => evt.id === matchingProduct.eventId);
    }

    let matchingSlot: Slot | null = null;
    if (orderItem.slotId !== null) {
      const result = this.slots.find(slot => slot.id === orderItem.slotId);
      matchingSlot = result ? result : null;
    }

    if (matchingProduct && matchingEvent) {
      let id = orderItem.id;
      if (!id.toString().startsWith('promo-')) {
        // set a new id, allows deleting this specific item etc.
        // DON'T use a random ID as this will differ in all instances from CartMixin
        id = `promo-${orderItem.id}-${promotion.code}`;
      }
      let ticketConfiguration: TicketConfiguration = new TicketConfiguration(
        id,
        matchingProduct,
        matchingEvent,
        matchingSlot,
        true,
        // TODO: don't display personal use here if there is a non-promoted config with personal use
        orderItem.personalUse,
        null,
        [],
        promotion,
        matchingProduct.registrationRequired,
      );
      ticketConfiguration.isPromoted = true;
      ticketConfiguration.quantity = quantity;
      ticketConfiguration.createdFrom = orderItem.id;

      const subTicketConfigurations: Array<TicketConfiguration> = [];
      if (orderItem.subOrderItems.length > 0) {
        orderItem.subOrderItems.forEach((subOrderItem) => {
          const subTicketConfiguration = this.createTicketConfigurationFromOrderItem(subOrderItem, ticketConfiguration);
          if (subTicketConfiguration !== null) {
            subTicketConfigurations.push(subTicketConfiguration);
          } else {
            // @TODO: Log - can this even happen?
            // This could happen if a subproduct does not yet have a matching product or event candidate
            // We don't reload them at the moment, because one shop is binded to one event
            // If a subproduct is related to a different event, we run into this condition
          }
        });
      }
      ticketConfiguration.subTicketConfigurations = subTicketConfigurations;

      return ticketConfiguration;
    }
    return null;
  }

  // @TODO: Merge this with the "createPromotionTicketConfigurationFromOrderItem" function
  createTicketConfigurationFromOrderItem(
    orderItem: OrderItem,
    parentConfig: TicketConfiguration | null = null,
    alteredQuantity: number | null = null,
  ): TicketConfiguration | null {
    const matchingProduct = this.products.find(prod => prod.id === orderItem.productId);
    let matchingEvent: Event | undefined;
    if (matchingProduct) {
      matchingEvent = this.events.find(evt => evt.id === matchingProduct.eventId);
    }

    let matchingSlot: Slot | null = null;
    if (orderItem.slotId !== null) {
      const result = this.slots.find(slot => slot.id === orderItem.slotId);
      matchingSlot = result ? result : null;
    }
    const isAdvertised = matchingProduct !== undefined && this.cart!.advertised.find(a => a.productId === matchingProduct.id) !== undefined;

    if (matchingProduct && matchingEvent && !isAdvertised) {
      let ticketConfiguration: TicketConfiguration = new TicketConfiguration(
        orderItem.id,
        matchingProduct,
        matchingEvent,
        matchingSlot,
        false,
        orderItem.personalUse,
        parentConfig ? parentConfig.id : null,
        [],
        null,
        matchingProduct.registrationRequired,
      );
      ticketConfiguration.quantity = alteredQuantity || orderItem.quantity;
      ticketConfiguration.initialQuantity = ticketConfiguration.quantity;
      ticketConfiguration.createdFrom = orderItem.id;

      const subTicketConfigurations: Array<TicketConfiguration> = [];
      if (orderItem.subOrderItems.length > 0) {
        orderItem.subOrderItems.forEach((subOrderItem) => {
          const subTicketConfiguration = this.createTicketConfigurationFromOrderItem(subOrderItem, ticketConfiguration);
          if (subTicketConfiguration !== null) {
            subTicketConfigurations.push(subTicketConfiguration);
          } else {
            // @TODO: Log - can this even happen?
            // This could happen if a subproduct does not yet have a matching product or event candidate
            // We don't reload them at the moment, because one shop is binded to one event
            // If a subproduct is related to a different event, we run into this condition
          }
        });
      }
      ticketConfiguration.subTicketConfigurations = subTicketConfigurations;

      return ticketConfiguration;
    }
    return null;
  }

  async removeFromCart(ticketConfiguration: TicketConfiguration) {
    this.loading = true;
    const removedDTO = this.orderDTO.removeConfiguration(ticketConfiguration);

    if (removedDTO) {
      try {
        const affectedEditableTicketConfig = this.editableTicketConfigurations.find(c => c.id === removedDTO.id);
        if (affectedEditableTicketConfig) {
          affectedEditableTicketConfig.quantity = removedDTO.quantity;
        }
        this.editableTicketConfigurations = this.editableTicketConfigurations.filter(c => c.id !== ticketConfiguration.id);
        await this.updateCart();
        this.loading = false;
      } catch (e) {
        this.loading = false;
        this.showErrorToast(<string>this.$t('toasts.removeError'));
        this.orderDTO.addProduct((new TicketConfigurationDTO()).fromModel(ticketConfiguration));
      }
    } else {
      this.loading = false;
      this.showErrorToast(<string>this.$t('toasts.removeError'));
    }
  }

  async updateCart() {
    this.loading = true;
    // we need to add the promoted product ticketconfigurations back to the originals
    // before we splitted them in single ticket configurations for display purposes
    const promotedConfigs = this.editableTicketConfigurations.filter(ticketConfig => ticketConfig.isPromoted);
    promotedConfigs.forEach((promotedConfig: TicketConfiguration) => {
      // don't compare ticket config ids here as the promo configs got their own temporary id
      const originalTicketConfig = this.editableTicketConfigurations.find((config) => {
        if (promotedConfig.slot === null) {
          return config.product.id === promotedConfig.product.id && config.slot === null && !config.isPromoted;
        } else {
          if (config.slot === null) {
            return false;
          }

          return config.product.id === promotedConfig.product.id && config.slot.id === promotedConfig.slot.id && !config.isPromoted;
        }
      });
      if (originalTicketConfig) {
        originalTicketConfig.quantity = originalTicketConfig.quantity + promotedConfig.quantity;
        promotedConfig.quantity = 0;
      } else {
        // there is no original configuration because all have been promoted. Therefore, just switch the "isPromoted" flag
        // so it's not filtered out later on
        promotedConfig.isPromoted = false;
      }
    });

    if (promotedConfigs.length) {
      this.editableTicketConfigurations = this.editableTicketConfigurations.filter(ed => !ed.isPromoted);
    }

    this.orderDTO.refreshCart(this.editableTicketConfigurations);
    try {
      const cart = await this.orderService.updateCart(this.orderDTO, this.userLanguage);
      const inventoryUrl = this.inventoryUrl;
      await this.$store.dispatch('fetchInitialData', {inventoryUrl, cart});
      this.$store.commit(UPDATE_CART, cart);
      if (cart.failedSlotUpdates.length === 0) {
        this.showToast(<string>this.$t('toasts.shoppingCartUpdated'));
      } else {
        let distinctProducts: any = {};
        cart.failedSlotUpdates.forEach((fs) => {
          if (fs.ticketConfig && fs.ticketConfig.product) {
            const prod = fs.ticketConfig.product;
            if (!(prod.id in distinctProducts)) {
              distinctProducts[prod.id] = prod;
            }
          }
        });

        const products: Array<Product> = Object.values(distinctProducts);

        const productTitles: Array<string> = products.map(p => p.descriptionWebShop);
        if (productTitles.length === 0) {
          this.showToast(<string>this.$t('toasts.contingentTicket'));
        } else if (productTitles.length === 1) {
          this.showToast(<string>this.$t('toasts.contingentProduct', {productTitle: productTitles[0]}));
        } else {
          this.showToast(<string>this.$t('toasts.contingentProducts', {productTitle: productTitles.join('", "')}));
        }
      }
    } catch (e) {
      console.error(e);
      (new ApiErrorHandler()).handle(e);
      this.loading = false;
      // rollback changes
    }
  }

  @Watch('cart')
  onCartChange(newVal, oldVal) {
    // deletion of cart is triggered in the MiniCartView, therefore as soon as the cart gets deleted,
    // we need to reset the inner state of this component, too
    if (oldVal !== null && newVal === null) {
      this.deleteOrder();
    }
  }

  deleteOrder() {
    localStorage.removeItem('dataLayerData');
    if (this.isGuestOrder) {
      this.$store.commit(CLEAR_BUYER_DATA);
    }
    this.$store.commit(DELETE_CART);
    this.$store.commit(CLEAR_IS_GUEST_ORDER); // See #LMSTICKET-877
    this.orderDTO = new OrderDTO();
    this.ticketConfigurations = [];
    this.advertisedOrderItems = [];
    this.editableTicketConfigurations = [];
    this.fixedTicketConfigurations = [];
    CartTimeUtility.getInstance().shutdown();
    this.orderChangedDate = null;
    this.cartTimeInterval = null;
  }

  findProductPromotion(id: string | number): any {
    const numericId = parseInt(id.toString(), 10);
    return this.productPromotionMapping.find(p => p.productId === numericId);
  }

  getClient(clientId: number | string): Client | null {
    return this.clients.find(c => c.id === clientId) || null;
  }

  get computedEditableTicketConfigurationsByEvent(): { [eventId: number]: TicketConfiguration[] } {
    const confs = this.computedEditableTicketConfigurations;
    let eventIds = confs.map(c => c.event.id);
    eventIds = [...new Set(eventIds)];

    const objs: { [eventId: number]: TicketConfiguration[] } = {};
    eventIds.forEach((eid) => {
      if (!(eid in objs)) {
        objs[eid] = [];
      }

      objs[eid] = confs.filter(tc => tc.event.id === eid);
    });

    return objs;
  }

  get computedEditableTicketConfigurations(): TicketConfiguration[] {
    if (!this.checkoutView) {
      return this.editableTicketConfigurations;
    }
    const configurationLookup: { [id: string]: TicketConfiguration } = {};
    let lookup: any = {};
    /**
     * in checkout view the products need to be displayed differently, so we assemble them in a different way
     */
    this.orderDTO.additionalOrderItemConfigurations.forEach((aoic) => {
      const tc = this.editableTicketConfigurations.find(t => t.id === aoic.orderItemId || (t.id as string).includes(aoic.orderItemId as string));
      if (tc) {
        if (!(tc.id in lookup)) {
          lookup[tc.id] = UUID4Utility.uuid4();
        }
        const fakeId = lookup[tc.id];
        const cloneConf = TicketConfiguration.clone(tc);
        cloneConf.id = fakeId;

        /**
         * if the product does not have registration required or local pub trans, it is displayed like default
         * 1 orderItem with quantity x
         */
        if (!tc.isRegistrationRequired && !tc.product.localPubTransIncluded) {
          if (!(fakeId in configurationLookup)) {
            configurationLookup[fakeId] = tc;
          }
          return;
        }


        cloneConf.quantity = 1;

        /**
         * in case the product has registration required / pubtrans, we add all products to one orderItem
         * which are not flagged for personalUse / pubtrans used by the buyer
         * 1 orderItem with quantity x minus quantity of personal used / pubtrans used
         */
        if (!aoic.personalUse && !aoic.publicTransportConfig?.usePubTrans) {
          if (!(fakeId in configurationLookup)) {
            configurationLookup[fakeId] = cloneConf;
          } else {
            configurationLookup[fakeId].quantity += 1;
          }

          return;
        }

        /**
         * in case the product has registration required / pubtrans, and the buyer has chosen personalUse or pubTrans
         * it will be displayed separately with quantity 1 and the personal information
         */
        cloneConf.id = aoic.id;
        configurationLookup[aoic.id] = cloneConf;
      }
    });

    return Object.values(configurationLookup);
  }

  get remainingTimeInSeconds(): number {
    this.triggerRefresh = !this.triggerRefresh;
    const cartTimeUtil = CartTimeUtility.getInstance();
    let time = 0;
    if (this.orderChangedDate) {
      time = cartTimeUtil.getRemainingTime(this.orderChangedDate);
      time = Number.isNaN(time) ? 0 : time;
    }
    return time;
  }

  get eventLookup(): { [id: number]: Event } {
    const lookup: { [id: number]: Event } = {};
    this.events.forEach((ev) => {
      if (!(ev.id in lookup)) {
        lookup[ev.id] = ev;
      }
    });

    return lookup;
  }

  get cartTimeoutString(): string {
    if (this.orderChangedDate && CartTimeUtility.getInstance().currentDate !== null) {
      const minutesDiff = Math.floor(this.remainingTimeInSeconds / 60);
      let secondsDiff = this.remainingTimeInSeconds - minutesDiff * 60;
      let secondsString = `${secondsDiff}`;
      if (secondsDiff < 10) {
        secondsString = `0${secondsDiff}`;
      }
      let minutesString = `${minutesDiff}`;
      if (minutesDiff < 10) {
        minutesString = `0${minutesDiff}`;
      }
      return `${minutesString}:${secondsString}`;
    }
    return '';
  }

  get totalProductCount(): number {
    let productCount: number = 0;
    if (this.currentOrder) {
      this.currentOrder.orderItems.forEach((orderItem: OrderItem) => {
        productCount += orderItem.quantity;
      });
    }

    return productCount;
  }

  @Watch('currentOrder.orderItems')
  async updateOrderItems(value: Array<OrderItem>, oldValue: Array<OrderItem>) {
    if (!this.initializing && typeof value !== 'undefined') {
      await this.updateTicketConfigurations();
    }
  }
}
